/**
 * Canonical JSON serialization for stable checksums.
 * Sorts object keys recursively; preserves array order.
 */
export function canonicalize(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => canonicalize(entry));
  }

  const record = value as Record<string, unknown>;
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(record).sort()) {
    sorted[key] = canonicalize(record[key]);
  }
  return sorted;
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function toHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function digestSha256(text: string): Promise<string> {
  const bytes = new globalThis.TextEncoder().encode(text);

  if (typeof globalThis.crypto?.subtle?.digest !== 'function') {
    throw new Error('SHA-256 digest is unavailable in this environment');
  }

  const hash = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return toHex(hash);
}

export async function computeChecksum(payload: unknown): Promise<string> {
  return digestSha256(canonicalJson(payload));
}

export async function verifyChecksum(payload: unknown, checksum: string): Promise<boolean> {
  const expected = await computeChecksum(payload);
  return expected === checksum;
}
